import React, { useEffect, useState } from 'react';
import { Container, Card, Form, Button, Alert, Row, Col, Spinner, Badge } from 'react-bootstrap';
import { useAppDispatch, useAppSelector } from '../redux/hooks';
import { fetchOrganization, updateOrganization, clearError } from '../redux/slices/organizationSlice';

const OrganizationSettings: React.FC = () => {
  const dispatch = useAppDispatch();
  const { organization, loading, error } = useAppSelector((state) => state.organization);
  const currentUser = useAppSelector((state) => state.auth.user);

  const [formData, setFormData] = useState({
    name: '',
    logo: '',
    contact_email: '',
    contact_phone: '',
    address: '',
    city: '',
    country: ''
  });

  const [successMessage, setSuccessMessage] = useState('');

  useEffect(() => {
    dispatch(fetchOrganization());
  }, [dispatch]);

  useEffect(() => {
    if (organization) {
      setFormData({
        name: organization.name || '',
        logo: organization.logo || '',
        contact_email: organization.contact_email || '',
        contact_phone: organization.contact_phone || '',
        address: organization.address || '',
        city: organization.city || '',
        country: organization.country || ''
      });
    }
  }, [organization]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => {
        dispatch(clearError());
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [error, dispatch]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSuccessMessage('');

    try {
      await dispatch(updateOrganization(formData)).unwrap();
      setSuccessMessage('Organization settings updated successfully');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      // Error is handled by Redux
    }
  };

  const isAdmin = currentUser?.role === 'admin';

  if (loading && !organization) {
    return (
      <div style={{ backgroundColor: '#f5f5f5', minHeight: '100vh', paddingTop: '2rem' }}>
        <Container>
          <div className="text-center" style={{ padding: '60px 0' }}>
            <Spinner animation="border" style={{ color: '#00579a', width: '40px', height: '40px' }} />
            <p style={{ marginTop: '16px', color: '#8c8c8c' }}>Loading organization...</p>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div style={{ backgroundColor: '#f5f5f5', minHeight: '100vh', paddingTop: '2rem', paddingBottom: '2rem' }}>
      <Container>
        <div className="mb-4">
          <h1 style={{
            color: '#00579a',
            fontWeight: 600,
            fontSize: '28px',
            margin: 0
          }}>
            Organization Settings
          </h1>
        </div>

        {error && (
          <Alert
            variant="danger"
            dismissible
            onClose={() => dispatch(clearError())}
            style={{
              borderRadius: '6px',
              border: '1px solid #ffccc7',
              backgroundColor: '#fff2f0',
              marginBottom: '1.5rem'
            }}
          >
            {error}
          </Alert>
        )}

        {successMessage && (
          <Alert
            variant="success"
            dismissible
            onClose={() => setSuccessMessage('')}
            style={{
              borderRadius: '6px',
              border: '1px solid #b7eb8f',
              backgroundColor: '#f6ffed',
              marginBottom: '1.5rem'
            }}
          >
            {successMessage}
          </Alert>
        )}

        {!isAdmin && (
          <Alert variant="warning" style={{ marginBottom: '1.5rem' }}>
            Only administrators can modify organization settings. You can view the information below.
          </Alert>
        )}

        {/* Organization Info Card */}
        <Card style={{
          border: 'none',
          borderRadius: '8px',
          boxShadow: '0 1px 2px rgba(0,0,0,0.03), 0 1px 6px rgba(0,0,0,0.06)',
          marginBottom: '1.5rem'
        }}>
          <Card.Body style={{ padding: '24px' }}>
            <h5 style={{ color: '#00579a', fontWeight: 600, marginBottom: '20px' }}>
              Organization Information
            </h5>
            <Row className="mb-3">
              <Col md={6}>
                <div style={{ marginBottom: '12px' }}>
                  <strong style={{ fontSize: '14px', color: '#595959' }}>Domain:</strong>
                  <div style={{ fontSize: '16px', color: '#262626', marginTop: '4px' }}>
                    @{organization?.domain}
                  </div>
                </div>
              </Col>
              <Col md={6}>
                <div style={{ marginBottom: '12px' }}>
                  <strong style={{ fontSize: '14px', color: '#595959' }}>Status:</strong>
                  <div style={{ marginTop: '4px' }}>
                    <Badge style={{
                      backgroundColor: organization?.status === 'active' ? '#f6ffed' : '#fff7e6',
                      color: organization?.status === 'active' ? '#52c41a' : '#fa8c16',
                      fontWeight: 500,
                      padding: '4px 10px',
                      borderRadius: '4px',
                      fontSize: '12px',
                      textTransform: 'capitalize'
                    }}>
                      {organization?.status}
                    </Badge>
                  </div>
                </div>
              </Col>
            </Row>
            <Row>
              <Col md={6}>
                <div style={{ marginBottom: '12px' }}>
                  <strong style={{ fontSize: '14px', color: '#595959' }}>Created:</strong>
                  <div style={{ fontSize: '14px', color: '#262626', marginTop: '4px' }}>
                    {organization?.created_at && new Date(organization.created_at).toLocaleDateString()}
                  </div>
                </div>
              </Col>
              <Col md={6}>
                <div style={{ marginBottom: '12px' }}>
                  <strong style={{ fontSize: '14px', color: '#595959' }}>Last Updated:</strong>
                  <div style={{ fontSize: '14px', color: '#262626', marginTop: '4px' }}>
                    {organization?.updated_at && new Date(organization.updated_at).toLocaleDateString()}
                  </div>
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Settings Form */}
        <Card style={{
          border: 'none',
          borderRadius: '8px',
          boxShadow: '0 1px 2px rgba(0,0,0,0.03), 0 1px 6px rgba(0,0,0,0.06)'
        }}>
          <Card.Body style={{ padding: '24px' }}>
            <Form onSubmit={handleSubmit}>
              <Row>
                <Col md={6}>
                  <h6 style={{ color: '#00579a', fontWeight: 600, marginBottom: '16px' }}>
                    Basic Information
                  </h6>
                  <Form.Group className="mb-3">
                    <Form.Label style={labelStyle}>Organization Name *</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      required
                      disabled={!isAdmin}
                      style={inputStyle}
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label style={labelStyle}>Logo URL</Form.Label>
                    <Form.Control
                      type="url"
                      value={formData.logo}
                      onChange={(e) => setFormData({ ...formData, logo: e.target.value })}
                      disabled={!isAdmin}
                      style={inputStyle}
                      placeholder="https://example.com/logo.png"
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label style={labelStyle}>Contact Email</Form.Label>
                    <Form.Control
                      type="email"
                      value={formData.contact_email}
                      onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                      disabled={!isAdmin}
                      style={inputStyle}
                      placeholder="contact@organization.com"
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label style={labelStyle}>Contact Phone</Form.Label>
                    <Form.Control
                      type="tel"
                      value={formData.contact_phone}
                      onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                      disabled={!isAdmin}
                      style={inputStyle}
                      placeholder="+264 61 123 4567"
                    />
                  </Form.Group>
                </Col>

                <Col md={6}>
                  <h6 style={{ color: '#00579a', fontWeight: 600, marginBottom: '16px' }}>
                    Location Details
                  </h6>
                  <Form.Group className="mb-3">
                    <Form.Label style={labelStyle}>Address</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      value={formData.address}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      disabled={!isAdmin}
                      style={inputStyle}
                      placeholder="Street address"
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label style={labelStyle}>City</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.city}
                      onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                      disabled={!isAdmin}
                      style={inputStyle}
                      placeholder="Windhoek"
                    />
                  </Form.Group>

                  <Form.Group className="mb-3">
                    <Form.Label style={labelStyle}>Country</Form.Label>
                    <Form.Control
                      type="text"
                      value={formData.country}
                      onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                      disabled={!isAdmin}
                      style={inputStyle}
                      placeholder="Namibia"
                    />
                  </Form.Group>
                </Col>
              </Row>

              {isAdmin && (
                <div className="d-flex justify-content-end mt-4">
                  <Button
                    type="submit"
                    disabled={loading}
                    style={{
                      backgroundColor: '#00579a',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      padding: '10px 24px',
                      fontWeight: 500,
                      boxShadow: '0 2px 4px rgba(0,87,154,0.2)'
                    }}
                  >
                    {loading ? 'Saving...' : 'Save Changes'}
                  </Button>
                </div>
              )}
            </Form>
          </Card.Body>
        </Card>
      </Container>
    </div>
  );
};

const labelStyle: React.CSSProperties = {
  fontWeight: 500,
  color: '#262626',
  fontSize: '14px',
  marginBottom: '8px'
};

const inputStyle: React.CSSProperties = {
  borderRadius: '6px',
  border: '1px solid #d9d9d9',
  padding: '8px 12px',
  fontSize: '14px'
};

export default OrganizationSettings;